/**
 * HBE Entertainment logo — official brand artwork served from
 * /api/media/brand/*. Three variants:
 *
 *   variant="full"    →  crest + wordmark (vertical composition)
 *                        color: "navy" | "white"
 *   variant="mark"    →  emblem only (square)
 *                        color: "navy" | "white"
 *   variant="lockup"  →  emblem + "HBE" Playfair wordmark set horizontally.
 *                        Best for tight horizontal navbars where the full
 *                        crest's sub-text ("estd 2026", "ENTERTAINMENT")
 *                        wouldn't survive at nav-height.
 *
 * `size` is the pixel HEIGHT — width auto-scales.
 */
import { API } from "../lib/api";

// Bump when brand PNGs change so downstream caches (CF/browser) reload.
const V = "7";

const SOURCES = {
  "full-navy": `${API}/media/brand/hbe-full-navy.png?v=${V}`,
  "full-white": `${API}/media/brand/hbe-full-white.png?v=${V}`,
  "mark-navy": `${API}/media/brand/hbe-emblem.png?v=${V}`,
  "mark-white": `${API}/media/brand/hbe-emblem-white.png?v=${V}`,
};

export default function HbeLogo({
  variant = "full",
  color = "navy",
  size = 44,
  className = "",
  alt = "HBE Entertainment",
}) {
  if (variant === "lockup") {
    const isWhite = color === "white";
    const src = isWhite ? SOURCES["mark-white"] : SOURCES["mark-navy"];
    // Wordmark: Playfair Display, sized to sit next to the emblem cap-height.
    const textSize = Math.round(size * 0.62);
    return (
      <span
        className={`inline-flex items-center gap-2 select-none ${className}`}
        style={{ height: `${size}px` }}
        data-testid="hbe-logo"
        data-variant="lockup"
      >
        <img
          src={src}
          alt=""
          aria-hidden="true"
          draggable={false}
          style={{ height: `${size}px`, width: "auto" }}
        />
        <span
          className="font-brand leading-none"
          style={{
            fontSize: `${textSize}px`,
            fontWeight: 600,
            letterSpacing: "0.06em",
            color: isWhite ? "#ffffff" : "#062843",
            // Optical alignment with the emblem baseline
            transform: "translateY(1px)",
          }}
        >
          HBE
        </span>
        <span className="sr-only">{alt}</span>
      </span>
    );
  }

  const key =
    variant === "mark"
      ? color === "white"
        ? "mark-white"
        : "mark-navy"
      : `${variant}-${color}`;
  const src = SOURCES[key] || SOURCES["full-navy"];
  return (
    <img
      src={src}
      alt={alt}
      style={{ height: `${size}px`, width: "auto" }}
      className={`select-none ${className}`}
      draggable={false}
      data-testid="hbe-logo"
      data-variant={variant}
    />
  );
}
